import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/modules/shared/ui/components/dialog"
import { Button } from "@/modules/shared/ui/components/button"
import { Input } from "@/modules/shared/ui/components/input"
import { Label } from "@/modules/shared/ui/components/label"
import { Alert, AlertDescription } from "@/modules/shared/ui/components/alert"
import { Badge } from "@/modules/shared/ui/components/badge"
import { Spinner } from "@/modules/shared/ui/components/spinner"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { z } from "zod"
import { format } from "date-fns"
import { toast } from "sonner"
import axios from "axios"
import { HttpStatusCodes } from "@/lib/http-status-codes"
import { Fingerprint } from "lucide-react"
import {
  useGetIdVerification,
  useRunIdVerification,
} from "@/modules/recruitment/hooks/useIdVerification"
import { useQueryClient } from "@tanstack/react-query"
import { useEffect, useState } from "react"
import { IdVerificationResult } from "../../dtos/IdVerificationDto"

const schema = z.object({
  idNumber: z.string().regex(/^\d{13}$/, "Must be exactly 13 digits"),
})

type FormValues = z.infer<typeof schema>

interface IdVerificationDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  candidateId: string | undefined
  candidateName: string
  organizationId: string | undefined
  prefilledIdNumber?: string | null
  /** Called with the candidateId when a verification completes successfully */
  onVerificationCompleted?: (candidateId: string) => void
}

export function IdVerificationDialog({
  open,
  onOpenChange,
  candidateId,
  candidateName,
  organizationId,
  prefilledIdNumber,
  onVerificationCompleted,
}: IdVerificationDialogProps) {
  const queryClient = useQueryClient()
  const [showRunForm, setShowRunForm] = useState(false)

  const { data: existingResult, isLoading: isLoadingResult } =
    useGetIdVerification(organizationId, candidateId, open)

  const { mutate: runVerification, isPending } = useRunIdVerification(
    organizationId,
    candidateId,
  )

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
  })

  // Reset local state when dialog closes; seed the form with the extracted ID on open
  useEffect(() => {
    if (!open) {
      setShowRunForm(false)
      reset({ idNumber: "" })
    } else {
      reset({ idNumber: prefilledIdNumber ?? "" })
    }
  }, [open, prefilledIdNumber, reset])

  // Show form automatically if there's no completed result
  const showForm =
    showRunForm ||
    (!isLoadingResult &&
      (!existingResult || existingResult.status !== "Completed"))

  function onSubmit(values: FormValues) {
    runVerification(
      { idNumber: values.idNumber, forceRerun: showRunForm },
      {
        onSuccess: () => {
          toast.success("ID verification completed successfully")
          setShowRunForm(false)
          reset()
          if (candidateId) onVerificationCompleted?.(candidateId)
        },
        onError: (error: unknown) => {
          if (axios.isAxiosError(error)) {
            if (error.response?.status === HttpStatusCodes.CONFLICT) {
              toast.info(
                "This candidate has already been verified. Use 'Run Again' to re-verify.",
              )
              queryClient.invalidateQueries({
                queryKey: ["id-verification", candidateId],
              })
              setShowRunForm(false)
            } else if (error.response?.status === HttpStatusCodes.BAD_REQUEST) {
              const msg =
                error.response.data?.error ||
                error.response.data?.message ||
                "Verification failed"
              toast.error(msg)
            } else {
              toast.error("An unexpected error occurred")
            }
          } else {
            toast.error("An unexpected error occurred")
          }
        },
      },
    )
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md bg-card">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Fingerprint className="w-5 h-5 text-primary" />
            ID Verification
          </DialogTitle>
          <DialogDescription>
            Run an identity check for{" "}
            <span className="font-medium text-foreground">
              {candidateName}
            </span>{" "}
          </DialogDescription>
        </DialogHeader>

        {isLoadingResult ? (
          <div className="flex items-center justify-center py-10">
            <Spinner className="size-6" />
          </div>
        ) : showForm ? (
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            {showRunForm && existingResult && (
              <Alert>
                <AlertDescription>
                  A previous verification exists. Submitting will run a new
                  check and overwrite the cached result.
                </AlertDescription>
              </Alert>
            )}
            <div className="space-y-2">
              <Label htmlFor="idNumber">SA ID Number</Label>
              <Input
                id="idNumber"
                placeholder="e.g. 9001015000000"
                maxLength={13}
                {...register("idNumber")}
              />
              {errors.idNumber && (
                <p className="text-sm text-destructive">
                  {errors.idNumber.message}
                </p>
              )}
              <p className="text-xs text-muted-foreground">
                Must be exactly 13 digits.
              </p>
            </div>
            <DialogFooter className="gap-2">
              {showRunForm && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowRunForm(false)}
                  disabled={isPending}
                >
                  Cancel
                </Button>
              )}
              <Button type="submit" disabled={isPending}>
                {isPending && <Spinner className="size-4 mr-2" />}
                {showRunForm ? "Run Again" : "Run Verification"}
              </Button>
            </DialogFooter>
          </form>
        ) : (
          existingResult && (
            <VerificationResult
              result={existingResult}
              onRunAgain={() => {
                reset()
                setShowRunForm(true)
              }}
            />
          )
        )}
      </DialogContent>
    </Dialog>
  )
}

interface VerificationResultProps {
  result: IdVerificationResult
  onRunAgain: () => void
}

function VerificationResult({ result, onRunAgain }: VerificationResultProps) {
  const fullName =
    result.firstName || result.surname
      ? [result.firstName, result.surname].filter(Boolean).join(" ")
      : "Not available"

  const formattedDob = result.dateOfBirth
    ? (() => {
        try {
          return format(new Date(result.dateOfBirth), "dd MMM yyyy")
        } catch {
          return result.dateOfBirth
        }
      })()
    : "Not available"

  const formattedRunAt = (() => {
    try {
      return format(new Date(result.runAt), "dd MMM yyyy HH:mm")
    } catch {
      return result.runAt
    }
  })()

  const statusColors: Record<string, string> = {
    Completed: "bg-green-100 text-green-800 border-green-200",
    NoResults: "bg-muted text-foreground border-border",
    NoCredits: "bg-orange-100 text-orange-800 border-orange-200",
    LicenseError: "bg-red-100 text-red-800 border-red-200",
    Failed: "bg-red-100 text-red-800 border-red-200",
  }

  return (
    <div className="space-y-4">
      {/* Result fields */}
      <div className="rounded-lg border bg-muted/30 p-4 space-y-3">
        <div className="flex items-center justify-between">
          <span className="text-xs text-muted-foreground font-medium uppercase tracking-wide">
            Status
          </span>
          <Badge
            variant="outline"
            className={statusColors[result.status] ?? ""}
          >
            {result.status}
          </Badge>
        </div>

        <ResultRow label="Full Name" value={fullName} />
        <ResultRow label="ID Number" value={result.idNumber} />
        <ResultRow label="Date of Birth" value={formattedDob} />
        <ResultRow label="Gender" value={result.gender ?? "Not available"} />
        <ResultRow
          label="Deceased"
          value={result.isDeceased ? "Yes" : "No"}
          className={result.isDeceased ? "text-red-600 font-semibold" : ""}
        />
        <ResultRow
          label="Adverse Indicator"
          value={result.hasAdverseIndicator ? "Yes" : "No"}
          className={
            result.hasAdverseIndicator ? "text-amber-600 font-semibold" : ""
          }
        />
        <ResultRow
          label="Verified At"
          value={formattedRunAt}
          className="text-xs text-muted-foreground"
        />
      </div>

      <DialogFooter>
        <Button variant="outline" onClick={onRunAgain}>
          Run Again
        </Button>
      </DialogFooter>
    </div>
  )
}

function ResultRow({
  label,
  value,
  className,
}: {
  label: string
  value: string
  className?: string
}) {
  return (
    <div className="flex items-center justify-between gap-4">
      <span className="text-sm text-muted-foreground shrink-0">{label}</span>
      <span className={`text-sm font-medium text-right ${className ?? ""}`}>
        {value}
      </span>
    </div>
  )
}
